import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { History, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { uiLocale } from "@/i18n";
import type { TemplateVersion } from "./types";

interface VersionHistoryModalProps {
  templateId: string;
  onClose: () => void;
  onRestore: () => void;
}

export default function VersionHistoryModal({
  templateId,
  onClose,
  onRestore,
}: VersionHistoryModalProps) {
  const { t } = useTranslation();
  const { data: versionsData, isLoading } = useQuery({
    queryKey: ["template-versions", templateId],
    queryFn: () =>
      api.get<ApiResponse<TemplateVersion[]>>(`/api/admin/templates/${templateId}/versions`),
  });

  const restoreMutation = useMutation({
    mutationFn: (versionId: string) =>
      api.post(`/api/admin/templates/${templateId}/restore/${versionId}`),
    onSuccess: () => {
      toast.success(t("opsAdmin.templates.versionRestored"));
      onRestore();
    },
    onError: (error: Error) => {
      toast.error(error.message || t("opsAdmin.templates.restoreFailed"));
    },
  });

  const versions = versionsData?.data || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
          <History className="mr-2 inline h-6 w-6" />
          {t("opsAdmin.templates.versionHistory")}
        </h2>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
          </div>
        ) : versions.length === 0 ? (
          <p className="text-gray-500">{t("opsAdmin.templates.noVersions")}</p>
        ) : (
          <div className="space-y-3">
            {versions.map((v, index) => (
              <div
                key={v.id}
                className="flex items-center justify-between rounded-lg border border-gray-200 p-4 dark:border-gray-700"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {t("opsAdmin.templates.versionN", { version: v.version })}
                    </span>
                    {index === 0 && (
                      <span className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded px-2 py-0.5 text-xs">
                        {t("opsAdmin.templates.current")}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    {v.changeNote || t("opsAdmin.templates.noNote")}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(v.createdAt).toLocaleString(uiLocale())}
                  </p>
                </div>
                {index > 0 && (
                  <button
                    onClick={() => restoreMutation.mutate(v.id)}
                    disabled={restoreMutation.isPending}
                    className="btn-ghost btn-sm"
                  >
                    {t("opsAdmin.templates.restore")}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn-ghost">
            {t("common.actions.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
